import { useState, useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Slider,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  FormControlLabel,
  Checkbox,
  CircularProgress,
  Autocomplete,
} from '@mui/material';
import type {
  Mitigation,
  CreateMitigationDto,
  UpdateMitigationDto,
} from 'shared';
import {
  MitigationStatus,
  ControlType,
  SuggestionPriority,
  getBestD3fendForIsoControl,
  getD3fendTechniqueById,
} from 'shared';
import { useIsoControls } from '../hooks/useRisks';

// =============================================================================
// TYPES
// =============================================================================

interface MitigationFormProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (data: CreateMitigationDto | UpdateMitigationDto) => Promise<void>;
  riskId: string;
  initialData?: Partial<Mitigation>;
  mode: 'create' | 'edit';
}

type CIADimension = 'confidentiality' | 'integrity' | 'availability';

interface MitigationFormData {
  title: string;
  description: string;
  controlType: ControlType;
  isoControlId: string;
  isoControlName: string;
  d3fendId: string;
  d3fendName: string;
  implementationStatus: MitigationStatus;
  reductionFactor: number;
  appliesTo: CIADimension[];
  estimatedCost: number;
  estimatedEffort: number;
  priority: SuggestionPriority;
  owner: string;
  dueDate: string;
}

// =============================================================================
// CONSTANTS
// =============================================================================

const DEFAULT_VALUES: MitigationFormData = {
  title: '',
  description: '',
  controlType: ControlType.PREVENTIVE,
  isoControlId: '',
  isoControlName: '',
  d3fendId: '',
  d3fendName: '',
  implementationStatus: MitigationStatus.PLANNED,
  reductionFactor: 0.5,
  appliesTo: ['confidentiality', 'integrity', 'availability'],
  estimatedCost: 0,
  estimatedEffort: 0,
  priority: SuggestionPriority.MEDIUM,
  owner: '',
  dueDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000)
    .toISOString()
    .split('T')[0], // 30 days from now
};

const CONTROL_TYPE_OPTIONS: ControlType[] = [
  ControlType.PREVENTIVE,
  ControlType.DETECTIVE,
  ControlType.CORRECTIVE,
  ControlType.COMPENSATING,
];

const MITIGATION_STATUS_OPTIONS: MitigationStatus[] = [
  MitigationStatus.PLANNED,
  MitigationStatus.IN_PROGRESS,
  MitigationStatus.IMPLEMENTED,
  MitigationStatus.VERIFIED,
];

const PRIORITY_OPTIONS: SuggestionPriority[] = [
  SuggestionPriority.HIGH,
  SuggestionPriority.MEDIUM,
  SuggestionPriority.LOW,
];

const CIA_DIMENSIONS: CIADimension[] = [
  'confidentiality',
  'integrity',
  'availability',
];

// =============================================================================
// MAIN COMPONENT
// =============================================================================

export function MitigationForm({
  open,
  onClose,
  onSubmit,
  riskId,
  initialData,
  mode,
}: MitigationFormProps) {
  const { t, i18n } = useTranslation();
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Fetch ISO 27001:2022 controls for autocomplete
  const { data: isoControls = [] } = useIsoControls();

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<MitigationFormData>({
    defaultValues: initialData
      ? {
          title: initialData.title ?? DEFAULT_VALUES.title,
          description: initialData.description ?? DEFAULT_VALUES.description,
          controlType: initialData.controlType ?? DEFAULT_VALUES.controlType,
          isoControlId: initialData.isoControlId ?? '',
          isoControlName: initialData.isoControlName ?? '',
          d3fendId: initialData.d3fendId ?? '',
          d3fendName: initialData.d3fendId
            ? (getD3fendTechniqueById(initialData.d3fendId)?.name ?? '')
            : '',
          implementationStatus:
            initialData.implementationStatus ??
            DEFAULT_VALUES.implementationStatus,
          reductionFactor:
            initialData.reductionFactor ?? DEFAULT_VALUES.reductionFactor,
          appliesTo: initialData.appliesTo ?? DEFAULT_VALUES.appliesTo,
          estimatedCost: initialData.estimatedCost ?? 0,
          estimatedEffort: initialData.estimatedEffort ?? 0,
          priority: initialData.priority ?? DEFAULT_VALUES.priority,
          owner: initialData.owner ?? DEFAULT_VALUES.owner,
          dueDate: initialData.dueDate
            ? new Date(initialData.dueDate).toISOString().split('T')[0]
            : DEFAULT_VALUES.dueDate,
        }
      : DEFAULT_VALUES,
  });

  // Reset form when initialData changes or modal opens
  useEffect(() => {
    if (open) {
      if (initialData) {
        reset({
          title: initialData.title ?? DEFAULT_VALUES.title,
          description: initialData.description ?? DEFAULT_VALUES.description,
          controlType: initialData.controlType ?? DEFAULT_VALUES.controlType,
          isoControlId: initialData.isoControlId ?? '',
          isoControlName: initialData.isoControlName ?? '',
          d3fendId: initialData.d3fendId ?? '',
          d3fendName: initialData.d3fendId
            ? (getD3fendTechniqueById(initialData.d3fendId)?.name ?? '')
            : '',
          implementationStatus:
            initialData.implementationStatus ??
            DEFAULT_VALUES.implementationStatus,
          reductionFactor:
            typeof initialData.reductionFactor === 'number'
              ? Math.max(0, Math.min(1, initialData.reductionFactor))
              : DEFAULT_VALUES.reductionFactor,
          appliesTo: initialData.appliesTo ?? DEFAULT_VALUES.appliesTo,
          estimatedCost: Math.max(0, Number(initialData.estimatedCost) || 0),
          estimatedEffort: Math.max(
            0,
            Number(initialData.estimatedEffort) || 0
          ),
          priority: initialData.priority ?? DEFAULT_VALUES.priority,
          owner: initialData.owner ?? DEFAULT_VALUES.owner,
          dueDate: initialData.dueDate
            ? new Date(initialData.dueDate).toISOString().split('T')[0]
            : DEFAULT_VALUES.dueDate,
        });
      } else {
        reset(DEFAULT_VALUES);
      }
    }
  }, [open, initialData, reset]);

  const watchedAppliesTo = watch('appliesTo');

  // Handle CIA dimension checkbox changes
  const handleCIAChange = (dimension: CIADimension, checked: boolean) => {
    const current = watchedAppliesTo || [];
    if (checked) {
      setValue('appliesTo', [...current, dimension]);
    } else {
      setValue(
        'appliesTo',
        current.filter(d => d !== dimension)
      );
    }
  };

  // Handle form submission
  const handleFormSubmit = async (data: MitigationFormData) => {
    setIsSubmitting(true);
    try {
      // Ensure numeric values are properly validated and converted
      const reductionFactor = Math.max(
        0,
        Math.min(1, Number(data.reductionFactor) || 0)
      );
      const estimatedCost = Math.max(0, Number(data.estimatedCost) || 0);
      const estimatedEffort = Math.max(0, Number(data.estimatedEffort) || 0);

      const mitigationData: CreateMitigationDto | UpdateMitigationDto = {
        title: data.title,
        description: data.description,
        controlType: data.controlType,
        isoControlId: data.isoControlId || undefined,
        isoControlName: data.isoControlName || undefined,
        d3fendId: data.d3fendId || undefined,
        implementationStatus: data.implementationStatus,
        reductionFactor,
        appliesTo: data.appliesTo,
        estimatedCost,
        estimatedEffort,
        priority: data.priority,
        owner: data.owner,
        dueDate: new Date(data.dueDate),
        ...(mode === 'create' && { riskId }),
      };

      await onSubmit(mitigationData);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  // Format ISO control for display
  const formatIsoControl = (control: {
    id: string;
    name: string;
    nameNb: string;
  }) => {
    const name = i18n.language === 'nb' ? control.nameNb : control.name;
    return `${control.id} - ${name}`;
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{ sx: { minHeight: '500px' } }}
    >
      <DialogTitle>
        {mode === 'create'
          ? t('mitigation.form.createTitle')
          : t('mitigation.form.editTitle')}
      </DialogTitle>

      <DialogContent>
        <Box
          component="form"
          onSubmit={handleSubmit(handleFormSubmit)}
          sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}
        >
          {/* Title */}
          <Controller
            name="title"
            control={control}
            rules={{ required: t('validation.required') }}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('mitigation.fields.title')}
                fullWidth
                error={!!errors.title}
                helperText={errors.title?.message}
              />
            )}
          />

          {/* Description */}
          <Controller
            name="description"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                label={t('mitigation.fields.description')}
                fullWidth
                multiline
                rows={2}
              />
            )}
          />

          {/* Control Type */}
          <Controller
            name="controlType"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>{t('mitigation.fields.controlType')}</InputLabel>
                <Select {...field} label={t('mitigation.fields.controlType')}>
                  {CONTROL_TYPE_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {t(`mitigation.controlType.${option}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          {/* ISO Control Reference (Autocomplete) */}
          <Controller
            name="isoControlId"
            control={control}
            render={({ field }) => {
              // Find the matching control object from the options
              const selectedControl = isoControls.find(
                c => (c as { id: string }).id === field.value
              );

              return (
                <Autocomplete
                  key={`iso-autocomplete-${isoControls.length}-${field.value}`}
                  options={isoControls}
                  getOptionLabel={option => {
                    if (typeof option === 'string') return option;
                    return formatIsoControl(
                      option as { id: string; name: string; nameNb: string }
                    );
                  }}
                  isOptionEqualToValue={(option, value) => {
                    if (!option || !value) return false;
                    const optionId =
                      typeof option === 'string'
                        ? option
                        : (option as { id: string }).id;
                    const valueId =
                      typeof value === 'string'
                        ? value
                        : (value as { id: string }).id;
                    return optionId === valueId;
                  }}
                  value={selectedControl || null}
                  // Show the ID as inputValue while loading or if control not found
                  inputValue={
                    selectedControl
                      ? formatIsoControl(
                          selectedControl as {
                            id: string;
                            name: string;
                            nameNb: string;
                          }
                        )
                      : field.value || ''
                  }
                  onInputChange={(_, _newInputValue, reason) => {
                    // Only update on user typing, not on option selection
                    if (reason === 'input' && !selectedControl) {
                      // User is typing - let them search
                    }
                  }}
                  onChange={(_, newValue) => {
                    if (newValue && typeof newValue !== 'string') {
                      const isoControl = newValue as {
                        id: string;
                        name: string;
                        nameNb: string;
                      };
                      field.onChange(isoControl.id);
                      // Store the name based on current language
                      const name =
                        i18n.language === 'nb'
                          ? isoControl.nameNb
                          : isoControl.name;
                      setValue('isoControlName', name);

                      // Auto-fill D3FEND technique based on ISO control mapping
                      const d3fendTechnique = getBestD3fendForIsoControl(
                        isoControl.id
                      );
                      if (d3fendTechnique) {
                        setValue('d3fendId', d3fendTechnique.id);
                        setValue('d3fendName', d3fendTechnique.name);
                      } else {
                        setValue('d3fendId', '');
                        setValue('d3fendName', '');
                      }
                    } else {
                      field.onChange('');
                      setValue('isoControlName', '');
                      setValue('d3fendId', '');
                      setValue('d3fendName', '');
                    }
                  }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('mitigation.fields.isoControlId')}
                      placeholder={t('mitigation.placeholders.isoControlId')}
                    />
                  )}
                />
              );
            }}
          />

          {/* MITRE D3FEND Technique (Auto-filled from ISO control mapping) */}
          <Controller
            name="d3fendId"
            control={control}
            render={({ field }) => (
              <TextField
                value={
                  field.value
                    ? `${field.value} - ${watch('d3fendName') || ''}`
                    : ''
                }
                label={t('mitigation.fields.d3fendId')}
                placeholder={t('mitigation.placeholders.d3fendId')}
                fullWidth
                disabled
                helperText={
                  field.value
                    ? t('mitigation.helpers.d3fendAutoFilled')
                    : t('mitigation.helpers.d3fendSelectIso')
                }
                InputProps={{
                  sx: {
                    bgcolor: field.value ? 'action.hover' : 'transparent',
                  },
                }}
              />
            )}
          />

          {/* Implementation Status */}
          <Controller
            name="implementationStatus"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>{t('mitigation.fields.status')}</InputLabel>
                <Select {...field} label={t('mitigation.fields.status')}>
                  {MITIGATION_STATUS_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {t(`mitigation.status.${option}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          {/* Effectiveness (Reduction Factor) */}
          <Box>
            <Typography gutterBottom>
              {t('mitigation.fields.reductionFactor')}:{' '}
              {Math.round((watch('reductionFactor') || 0) * 100)}%
            </Typography>
            <Controller
              name="reductionFactor"
              control={control}
              render={({ field }) => (
                <Slider
                  {...field}
                  min={0}
                  max={1}
                  step={0.05}
                  marks={[
                    { value: 0, label: '0%' },
                    { value: 0.5, label: '50%' },
                    { value: 1, label: '100%' },
                  ]}
                  valueLabelDisplay="auto"
                  valueLabelFormat={value => `${Math.round(value * 100)}%`}
                  onChange={(_, value) => field.onChange(value)}
                />
              )}
            />
          </Box>

          {/* Applies To (CIA) */}
          <Box>
            <Typography gutterBottom>
              {t('mitigation.fields.appliesTo')}
            </Typography>
            <FormGroup row>
              {CIA_DIMENSIONS.map(dimension => (
                <FormControlLabel
                  key={dimension}
                  control={
                    <Checkbox
                      checked={(watchedAppliesTo || []).includes(dimension)}
                      onChange={e =>
                        handleCIAChange(dimension, e.target.checked)
                      }
                    />
                  }
                  label={t(`common.cia.${dimension}`)}
                />
              ))}
            </FormGroup>
          </Box>

          {/* Cost and Effort */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="estimatedCost"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label={t('mitigation.fields.estimatedCost')}
                  fullWidth
                  InputProps={{ endAdornment: 'NOK' }}
                  inputProps={{ min: 0, step: 1000 }}
                  onChange={e => {
                    const value =
                      e.target.value === '' ? 0 : Number(e.target.value);
                    field.onChange(Math.max(0, value || 0));
                  }}
                />
              )}
            />

            <Controller
              name="estimatedEffort"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="number"
                  label={t('mitigation.fields.estimatedEffort')}
                  fullWidth
                  InputProps={{ endAdornment: t('mitigation.units.days') }}
                  inputProps={{ min: 0, step: 0.5 }}
                  onChange={e => {
                    const value =
                      e.target.value === '' ? 0 : Number(e.target.value);
                    field.onChange(Math.max(0, value || 0));
                  }}
                />
              )}
            />
          </Box>

          {/* Priority */}
          <Controller
            name="priority"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth>
                <InputLabel>{t('mitigation.fields.priority')}</InputLabel>
                <Select {...field} label={t('mitigation.fields.priority')}>
                  {PRIORITY_OPTIONS.map(option => (
                    <MenuItem key={option} value={option}>
                      {t(`common.priority.${option}`)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}
          />

          {/* Owner and Due Date */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Controller
              name="owner"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  label={t('mitigation.fields.owner')}
                  fullWidth
                  error={!!errors.owner}
                  helperText={errors.owner?.message}
                />
              )}
            />

            <Controller
              name="dueDate"
              control={control}
              rules={{ required: t('validation.required') }}
              render={({ field }) => (
                <TextField
                  {...field}
                  type="date"
                  label={t('mitigation.fields.dueDate')}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  error={!!errors.dueDate}
                  helperText={errors.dueDate?.message}
                />
              )}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={isSubmitting}>
          {t('common.cancel')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit(handleFormSubmit)}
          disabled={isSubmitting}
        >
          {isSubmitting ? <CircularProgress size={24} /> : t('common.save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default MitigationForm;
